import { RequestTypeEnum } from "@nlc/shared-types";
import { memo } from "react";

import type { RequestFilterType } from "../../../types/filters";
import type { TableConfig } from "../../../types/tables";

import {
  REQUEST_TYPE_TITLES,
  SUPERADMIN_REQUEST_TYPES,
} from "../../../config/requestTypeTitles";
import { useSuperAdminTabData } from "../../../hooks/shared/useSuperAdminTabData";
import { useSuperAdminRequestCounts } from "../../../hooks/superadmin/useSuperAdminRequestCounts";
import { usePerRequestTypeTableStates } from "../../../hooks/usePerRequestTypeTableStates";
import { LazyConfigurableTable } from "../../tables/LazyConfigurableTable";

type SuperAdminTabType =
  | "completed"
  | "due-today"
  | "open"
  | "overdue"
  | "paused"
  | "queued"
  | "ready-for-review";

interface UnifiedSuperAdminTabProps {
  lazyLoadingConfig?: {
    currentTab: string;
    enabled: boolean;
  };
  searchValue?: string;
  selectedFilter?: RequestFilterType;
  tableConfig: TableConfig;
  tabType: SuperAdminTabType;
}

function UnifiedSuperAdminTabComponent({
  lazyLoadingConfig,
  searchValue,
  selectedFilter,
  tableConfig,
  tabType,
}: UnifiedSuperAdminTabProps) {
  // Only load data if this tab is active (lazy loading)
  const isActiveTab = lazyLoadingConfig?.enabled
    ? lazyLoadingConfig.currentTab === tabType
    : true; // Default to active if lazy loading is not enabled

  // Individual table states for each request type
  const { createTableHandlers, tableStates } = usePerRequestTypeTableStates(
    tableConfig.defaultSort?.column || "createdAt",
    tableConfig.defaultSort?.direction === "desc" ? "descending" : "ascending",
  );

  // Fetch counts for pagination (with category breakdown)
  const { data: countsData, isLoading: isCountsLoading } =
    useSuperAdminRequestCounts({ enabled: isActiveTab, search: searchValue });

  // Unified data management with real-time updates (only if tab is active)
  const { requestTypeData } = useSuperAdminTabData({
    isActiveTab,
    searchValue,
    selectedFilter,
    tab: tabType,
    tableStates,
  });

  // Helper to get category-specific count for current tab
  const getCategoryCount = (category: string): number => {
    if (!countsData?.byCategory) return 0;

    const categoryData =
      countsData.byCategory[category as keyof typeof countsData.byCategory];

    if (!categoryData) return 0;

    // Map tab type to count field
    const tabToFieldMap: Record<SuperAdminTabType, keyof typeof categoryData> =
      {
        completed: "completed",
        "due-today": "dueToday",
        open: "open",
        overdue: "overdue",
        paused: "paused",
        queued: "queued",
        "ready-for-review": "readyForReview",
      };

    return categoryData[tabToFieldMap[tabType]] || 0;
  };

  // Create handlers map for each request type
  const requestTypeHandlers = {
    [RequestTypeEnum.GRAPHIC]: createTableHandlers(RequestTypeEnum.GRAPHIC),
    [RequestTypeEnum.VIDEO]: createTableHandlers(RequestTypeEnum.VIDEO),
    [RequestTypeEnum.WEBSITE_DESIGN]: createTableHandlers(
      RequestTypeEnum.WEBSITE_DESIGN,
    ),
    [RequestTypeEnum.WEBSITE_IMPLEMENTATION]: createTableHandlers(
      RequestTypeEnum.WEBSITE_IMPLEMENTATION,
    ),
  };

  return (
    <div className="space-y-6">
      {SUPERADMIN_REQUEST_TYPES.map((requestType) => {
        const requestTypeKey = requestType.replace("_", "-");
        const tableData = requestTypeData[requestType];
        const handlers = requestTypeHandlers[requestType];
        const currentTableState = tableStates[requestType];

        // When searching, use filtered counts from counts endpoint
        // When not searching, use category counts from counts endpoint
        const effectiveTotal = getCategoryCount(requestType);
        const effectiveCount = getCategoryCount(requestType);

        return (
          <div key={requestType}>
            <LazyConfigurableTable
              config={{
                ...tableConfig,
                id: `superadmin-${tabType}-${requestTypeKey}`,
                title: `${REQUEST_TYPE_TITLES[requestType]} requests`,
              }}
              count={effectiveCount}
              data={tableData?.data?.requests || []}
              isCountLoading={isCountsLoading}
              isLoading={tableData?.isLoading || false}
              paginationProps={{
                metadata: {
                  currentPage: currentTableState.currentPage,
                  itemsPerPage: currentTableState.itemsPerPage,
                  totalItems: effectiveTotal,
                  totalPages: Math.ceil(
                    effectiveTotal / currentTableState.itemsPerPage,
                  ),
                },
                onItemsPerPageChange: handlers.onItemsPerPageChange,
                onPageChange: handlers.onPageChange,
                pageSizeOptions: [10, 25, 50, 100],
                showPageSizeSelector: true,
              }}
              setSortDescriptor={handlers.onSortChange}
              sortDescriptor={currentTableState.sortDescriptor}
              useAdvancedSorting={true}
            />
          </div>
        );
      })}
    </div>
  );
}

export const UnifiedSuperAdminTab = memo(UnifiedSuperAdminTabComponent);
